import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  RefreshCw, TrendingUp, Gift, Crown, ArrowUpRight, Users, BookOpen,
  Loader2, Coins, Zap, Package, Calculator, Target } from "lucide-react";

const TIER_LABELS = ["Base rate", "+10% boost", "+20% boost", "+30% boost"];

export default function AgentHome() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [synced, setSynced] = useState(null);

  const load = async () => {
    const { data } = await api.get("/agent/dashboard");
    setData(data);
    setSynced(new Date());
  };
  useEffect(() => {
    load();
  }, []);

  if (!data)
    return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const b = data.breakdown;
  const acc = data.accelerator;
  const sym = data.symbol;
  const cur = data.currency;
  const m = (v) => money(v, cur, sym);

  const accPct = acc.has_next && acc.next_threshold
    ? Math.min(100, (data.collected_sales / acc.next_threshold) * 100)
    : 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">Welcome back</p>
          <h1 className="font-display text-xl font-bold tracking-tight">{user?.name?.split(" ")[0]}</h1>
        </div>
        <button onClick={load} className="p-2 rounded-full hover:bg-secondary" data-testid="refresh-button">
          <RefreshCw className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Hero */}
      <Card className="bg-primary text-primary-foreground p-6 relative overflow-hidden border-0" data-testid="hero-earnings">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
        <div className="absolute -right-2 top-12 w-24 h-24 rounded-full bg-white/5" />
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <p className="text-sm opacity-80">Total earnings · this month</p>
            {data.is_star_agent && (
              <Badge className="bg-amber-400 text-amber-950 border-0 gap-1"><Crown className="w-3 h-3" /> Star</Badge>
            )}
          </div>
          <p className="font-display text-5xl font-extrabold mt-2 tracking-tight" data-testid="hero-total">
            {m(data.total_earnings)}
          </p>
          <p className="text-xs opacity-70 mt-2">
            As of {new Date(data.as_of).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
            {" · "}{data.timezone}
          </p>
          <div className="flex gap-6 mt-5">
            <div>
              <p className="text-xs opacity-70">Bags sold</p>
              <p className="font-display text-lg font-bold">{data.bags_sold}</p>
            </div>
            <div>
              <p className="text-xs opacity-70">Collected sales</p>
              <p className="font-display text-lg font-bold">{m(data.collected_sales)}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Payout status */}
      <Card className="p-4 flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Payout status</span>
        <Badge variant={data.payout_status === "paid" ? "default" : "secondary"} className="capitalize" data-testid="payout-status">
          {data.payout_status}{data.payout_date ? ` · ${new Date(data.payout_date).toLocaleDateString()}` : ""}
        </Badge>
      </Card>

      {data.target && (
        <Card className="p-4" data-testid="target-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold flex items-center gap-1.5"><Target className="w-4 h-4 text-primary" /> My target · this month</p>
            {(data.target.sales_pct >= 100 || data.target.bags_pct >= 100) && <Badge>achieved</Badge>}
          </div>
          {data.target.sales_target != null && <TargetBar label="Collected sales" pct={data.target.sales_pct} actual={m(data.collected_sales)} target={m(data.target.sales_target)} gap={data.target.sales_gap > 0 ? `${m(data.target.sales_gap)} to go` : null} testid="target-sales" />}
          {data.target.bags_target != null && <TargetBar label="Bags" pct={data.target.bags_pct} actual={data.bags_sold} target={data.target.bags_target} gap={data.target.bags_gap > 0 ? `${data.target.bags_gap} bags to go` : null} testid="target-bags" />}
          {data.target.note && <p className="text-xs text-muted-foreground mt-2">{data.target.note}</p>}
        </Card>
      )}

      {/* Accelerator progress */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-accent" />
          <h3 className="font-semibold text-sm">Accelerator · {TIER_LABELS[acc.tier_index]}</h3>
        </div>
        {acc.has_next ? (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              {m(acc.remaining_to_next)} more in sales to reach {Math.round(acc.next_boost * 100)}% boost
            </p>
            <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-accent rounded-full transition-all duration-700" style={{ width: `${accPct}%` }} />
            </div>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">You're at the top tier — maximum boost applied!</p>
        )}
      </Card>

      {/* Breakdown cards */}
      <div className="grid grid-cols-2 gap-3">
        <Mini icon={Coins} label="Base commission" value={m(b.base_commission)} />
        <Mini icon={TrendingUp} label="Accelerator bonus" value={m(b.accelerator_bonus)} />
        <Mini icon={ArrowUpRight} label="Above-price bonus" value={m(b.above_price_bonus)} />
        <Mini icon={Gift} label="New-customer bonus" value={m(b.new_customer_bonus)} />
      </div>
      {b.deductions > 0 && (
        <Card className="p-4 flex items-center justify-between border-destructive/30">
          <span className="text-sm text-destructive">Deductions (returns/cancellations)</span>
          <span className="font-semibold text-destructive">−{m(b.deductions)}</span>
        </Card>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-4 gap-3">
        <QuickLink to="/app/bonuses" icon={Gift} label="Bonuses" />
        <QuickLink to="/app/customers" icon={Users} label="Customers" />
        <QuickLink to="/app/plan" icon={BookOpen} label="Plan" />
        <QuickLink to="/app/simulator" icon={Calculator} label="Simulate" />
      </div>

      <p className="text-center text-[11px] text-muted-foreground pt-2">
        Last synced {synced?.toLocaleTimeString()}
      </p>
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <Card className="p-4">
      <Icon className="w-4 h-4 text-primary mb-2" strokeWidth={1.7} />
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-display text-lg font-bold tracking-tight">{value}</p>
    </Card>
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link to={to} data-testid={`quicklink-${label.toLowerCase()}`}>
      <Card className="p-4 flex flex-col items-center gap-1.5 hover:bg-secondary/50 transition-colors">
        <Icon className="w-5 h-5 text-primary" strokeWidth={1.7} />
        <span className="text-xs font-medium">{label}</span>
      </Card>
    </Link>
  );
}

function TargetBar({ label, pct, actual, target, gap, testid }) {
  const tone = pct >= 100 ? "bg-primary" : pct >= 70 ? "bg-amber-500" : "bg-destructive";
  return (
    <div className="mt-2" data-testid={testid}>
      <div className="flex justify-between text-xs"><span className="text-muted-foreground">{label}</span><span className="font-medium">{actual} / {target} · {pct}%</span></div>
      <div className="h-2 rounded-full bg-secondary mt-1 overflow-hidden"><div className={`h-full ${tone}`} style={{ width: `${Math.min(100, pct || 0)}%` }} /></div>
      {gap && <p className="text-[11px] text-muted-foreground mt-0.5">{gap}</p>}
    </div>
  );
}
